import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FileText, Download, Upload, Paperclip } from 'lucide-react';
import QuestionBankService from '../../_services/QuestionBankService';
import type { QuestionRubric, QuestionReview } from '../../_services/QuestionBankService';
import { useITeachConfig } from '../../_context/PresetContext';
import type { QuestionAttributeBlockConfig } from '../../_context/PresetContext';
import { useAccessControl } from '../../access-control/AccessControlContext';

type BlockKey = 'questionDoc' | 'solutionVideo' | 'rubric' | 'reviews';
interface BlockPerm { visible: boolean; canEdit: boolean; }

interface QuestionExtrasPanelProps {
  questionId: number;
  /** Current question.SolutionLink (comma-separated). Used to list existing solution videos. */
  solutionLink?: string | null;
  /** Called after a doc upload replaces the question content, so the parent can refresh the view. */
  onReplaced?: () => void;
}

const btn = { margin: 2 } as const;

/** Extract video URLs from a comma-separated SolutionLink (legacy convention). */
function videoUrlsFrom(solutionLink?: string | null): string[] {
  if (!solutionLink) return [];
  return solutionLink.split(',').map(s => s.trim()).filter(Boolean)
    .filter(u => /\.mp4($|\?)/i.test(u) || /QUESTION_SOLUTION_VIDEOS/i.test(u));
}

function formatDate(raw: string): string {
  const d = new Date(raw);
  return isNaN(d.getTime()) ? raw : d.toLocaleString();
}

const QuestionExtrasPanel: React.FC<QuestionExtrasPanelProps> = ({ questionId, solutionLink, onReplaced }) => {
  const { config, loading: configLoading } = useITeachConfig();
  const { isAdmin, userRoleIds, loaded: aclLoaded } = useAccessControl();

  /**
   * Per-block visibility/edit gating driven by
   * customer_detail_master.JsonConfig.questionAttribute. Root (isAdmin) always
   * gets full view + edit; non-root users are gated by enableStatus + roles.
   */
  const permFor = useCallback((key: BlockKey): BlockPerm => {
    if (isAdmin) return { visible: true, canEdit: true };
    const block: QuestionAttributeBlockConfig | undefined = config.question?.[key];
    if (!block || block.enableStatus !== 1) return { visible: false, canEdit: false };
    const viewRoles = block.viewRoles || [];
    const editRoles = block.editRoles || [];
    const visible = viewRoles.length === 0 || viewRoles.some(r => userRoleIds.includes(r));
    const canEdit = editRoles.some(r => userRoleIds.includes(r));
    return { visible, canEdit };
  }, [isAdmin, userRoleIds, config.question]);

  const docPerm = permFor('questionDoc');
  const videoPerm = permFor('solutionVideo');
  const rubricPerm = permFor('rubric');
  const reviewPerm = permFor('reviews');

  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const flash = (type: 'success' | 'error', text: string) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };

  /* ---- Question Doc ---- */
  const [doc, setDoc] = useState<{ fileName: string; url: string } | null>(null);
  const [docFile, setDocFile] = useState<File | null>(null);
  const [docBusy, setDocBusy] = useState(false);
  const docInputRef = useRef<HTMLInputElement>(null);
  const [docDrag, setDocDrag] = useState(false);

  /* ---- Solution Video ---- */
  const [videoLink, setVideoLink] = useState<string | null>(solutionLink || null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoBusy, setVideoBusy] = useState(false);

  /* ---- Rubrics ---- */
  const [rubrics, setRubrics] = useState<QuestionRubric[]>([]);
  const [rubricCriteria, setRubricCriteria] = useState('');
  const [rubricScore, setRubricScore] = useState('');
  const [editingRubricId, setEditingRubricId] = useState<number | null>(null);
  const [rubricBusy, setRubricBusy] = useState(false);
  const [deleteRubricId, setDeleteRubricId] = useState<number | null>(null);

  /* ---- Reviews ---- */
  const [reviews, setReviews] = useState<QuestionReview[]>([]);
  const [newReview, setNewReview] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);

  const loadAll = useCallback(async () => {
    const [docRes, rubricRes, reviewRes] = await Promise.all([
      docPerm.visible ? QuestionBankService.getQuestionDoc(questionId) : Promise.resolve(null),
      rubricPerm.visible ? QuestionBankService.listRubrics(questionId) : Promise.resolve(null),
      reviewPerm.visible ? QuestionBankService.listReviews(questionId) : Promise.resolve(null),
    ]);
    if (docRes?.success) setDoc(docRes.data || null);
    if (rubricRes?.success) setRubrics(rubricRes.data || []);
    if (reviewRes?.success) setReviews(reviewRes.data || []);
  }, [questionId, docPerm.visible, rubricPerm.visible, reviewPerm.visible]);

  useEffect(() => {
    setVideoLink(solutionLink || null);
    loadAll();
  }, [loadAll, solutionLink]);

  /* ---- Doc handlers ---- */
  const handleDocUpload = async () => {
    if (!docFile) return;
    if (!docFile.name.toLowerCase().endsWith('.docx')) { flash('error', 'Only .docx files are supported'); return; }
    setDocBusy(true);
    const res = await QuestionBankService.uploadQuestionDoc(questionId, docFile);
    setDocBusy(false);
    if (res.success) {
      setDocFile(null);
      if (res.warning) {
        flash('error', res.warning);
      } else if (res.data?.replaced) {
        flash('success', 'Question and choices replaced from document');
        onReplaced?.(); // ask parent to refresh the question view
      } else {
        flash('success', 'Question doc uploaded');
      }
      const docRes = await QuestionBankService.getQuestionDoc(questionId);
      if (docRes.success) setDoc(docRes.data || null);
    } else {
      flash('error', res.error || 'Upload failed');
    }
  };

  /* ---- Video handlers ---- */
  const handleVideoUpload = async () => {
    if (!videoFile) return;
    if (!videoFile.name.toLowerCase().endsWith('.mp4')) { flash('error', 'Only .mp4 files are supported'); return; }
    setVideoBusy(true);
    const res = await QuestionBankService.uploadSolutionVideo(questionId, videoFile);
    setVideoBusy(false);
    if (res.success) {
      setVideoFile(null);
      setVideoLink(res.data?.solutionLink || videoLink);
      flash('success', 'Solution video uploaded');
    } else {
      flash('error', res.error || 'Upload failed');
    }
  };

  /* ---- Rubric handlers ---- */
  const resetRubricForm = () => { setEditingRubricId(null); setRubricCriteria(''); setRubricScore(''); };

  const handleRubricSubmit = async () => {
    if (!rubricCriteria.trim()) { flash('error', 'Evaluation criteria is required'); return; }
    setRubricBusy(true);
    const payload = { evaluationCriteria: rubricCriteria.trim(), score: Number(rubricScore) || 0 };
    const res = editingRubricId
      ? await QuestionBankService.updateRubric(editingRubricId, payload)
      : await QuestionBankService.addRubric(questionId, payload);
    setRubricBusy(false);
    if (res.success) {
      resetRubricForm();
      flash('success', editingRubricId ? 'Rubric updated' : 'Rubric added');
      const r = await QuestionBankService.listRubrics(questionId);
      if (r.success) setRubrics(r.data || []);
    } else {
      flash('error', res.error || 'Save failed');
    }
  };

  const startEditRubric = (r: QuestionRubric) => {
    setEditingRubricId(r.id);
    setRubricCriteria(r.evaluationCriteria || '');
    setRubricScore(String(r.score ?? ''));
  };

  const confirmDeleteRubric = async () => {
    if (deleteRubricId == null) return;
    const res = await QuestionBankService.deleteRubric(deleteRubricId);
    setDeleteRubricId(null);
    if (res.success) {
      flash('success', 'Rubric deleted');
      setRubrics(prev => prev.filter(r => r.id !== deleteRubricId));
      if (editingRubricId === deleteRubricId) resetRubricForm();
    } else {
      flash('error', res.error || 'Delete failed');
    }
  };

  /* ---- Review handlers ---- */
  const handleAddReview = async () => {
    if (!newReview.trim()) { flash('error', 'Review comment is required'); return; }
    setReviewBusy(true);
    const res = await QuestionBankService.addReview(questionId, newReview.trim());
    setReviewBusy(false);
    if (res.success) {
      setNewReview('');
      setReviews(res.data || []);
      flash('success', 'Review added');
    } else {
      flash('error', res.error || 'Failed to add review');
    }
  };

  const titleStyle: React.CSSProperties = { fontWeight: 700, fontSize: 14, color: 'var(--brand-accent)', marginBottom: 10 };
  const card: React.CSSProperties = { background: 'var(--surface-card)', border: '1px solid #ebeef5', borderRadius: 12, padding: 18, marginTop: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.04)' };
  const videos = videoUrlsFrom(videoLink);
  const anyVisible = docPerm.visible || videoPerm.visible || rubricPerm.visible || reviewPerm.visible;

  // Wait for config + access control to resolve before deciding what to show
  // (avoids briefly flashing blocks that a non-root user shouldn't see).
  if (configLoading || !aclLoaded) return null;
  if (!anyVisible) return null;

  return (
    <div>
      {msg && (
        <div style={{
          marginTop: 12, padding: '8px 12px', borderRadius: 8, fontSize: 13,
          background: msg.type === 'success' ? '#ecfdf5' : '#fef2f2',
          color: msg.type === 'success' ? '#065f46' : '#991b1b',
          border: `1px solid ${msg.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
        }}>{msg.text}</div>
      )}

      {/* ---- Question Doc ---- */}
      {docPerm.visible && (
      <div style={card}>
        <div style={titleStyle}>Question Doc</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {doc ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 14, width: '100%',
              border: '1px solid var(--border-default)', borderRadius: 10, padding: '12px 14px',
              background: 'var(--surface-page)',
            }}>
              {/* DOCX file-icon tile */}
              <div style={{
                flexShrink: 0, width: 44, height: 44, borderRadius: 8,
                background: 'linear-gradient(135deg,var(--brand-primary),var(--brand-primary-hover))',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', color: 'var(--text-on-brand)',
              }}>
                <FileText size={20} />
                <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: 0.5 }}>DOCX</span>
              </div>
              {/* Filename + meta (truncates long names, full name in title tooltip) */}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{
                  fontWeight: 600, fontSize: 13, color: 'var(--text-strong)',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }} title={doc.fileName}>{doc.fileName}</div>
                <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 2 }}>Word Document · DOCX</div>
              </div>
              {/* Solid primary Download button (opens presigned URL → forces download) */}
              <Button
                as="a" href={doc.url} target="_blank" rel="noopener noreferrer"
                variant="primary" size="sm"
                style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Download size={14} /> Download
              </Button>
            </div>
          ) : (
            <span style={{ color: 'var(--text-faint)', fontSize: 13 }}>No document attached</span>
          )}
        </div>
        {docPerm.canEdit && (
        <div style={{ marginTop: 12 }}>
          {/* Caption — tells the user this control updates the Question Doc */}
          <div style={{ fontSize: 11, color: 'var(--text-faint)', margin: '2px 0 8px' }}>
            Upload a new .docx to replace the current document.
          </div>
          <input
            ref={docInputRef}
            type="file"
            accept=".docx"
            style={{ display: 'none' }}
            onChange={(e: any) => setDocFile(e.target.files?.[0] || null)}
          />
          <div
            onClick={() => docInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDocDrag(true); }}
            onDragLeave={() => setDocDrag(false)}
            onDrop={(e) => {
              e.preventDefault(); setDocDrag(false);
              const f = e.dataTransfer.files?.[0];
              if (f) setDocFile(f);
            }}
            style={{
              cursor: 'pointer', borderRadius: 10, padding: '18px 14px', textAlign: 'center',
              border: `2px dashed ${docDrag ? '#7c3aed' : '#cbd5e1'}`,
              background: docDrag ? '#faf5ff' : '#fafafa', transition: 'all .15s ease',
            }}
          >
            <Upload size={22} color="var(--brand-accent)" />
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-body)', marginTop: 6 }}>
              Click to choose a .docx file
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 2 }}>or drag it here</div>
          </div>
          {docFile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
                background: 'var(--brand-primary-subtle)', color: 'var(--brand-primary-hover)', borderRadius: 999, padding: '4px 12px',
              }}>
                <Paperclip size={13} /> {docFile.name}
              </span>
              <Button variant="primary" size="sm" style={btn} disabled={docBusy} onClick={handleDocUpload}>
                {docBusy ? <Spinner animation="border" size="sm" /> : 'Update'}
              </Button>
            </div>
          )}
        </div>
        )}
      </div>
      )}

      {/* ---- Solution Video ---- */}
      {videoPerm.visible && (
      <div style={card}>
        <div style={titleStyle}>Solution Video</div>
        {videos.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {videos.map((u, i) => (
              <video key={i} src={u} controls style={{ width: '100%', maxWidth: 480, borderRadius: 8, background: '#000' }} />
            ))}
          </div>
        ) : (
          <span style={{ color: 'var(--text-faint)', fontSize: 13 }}>No solution video uploaded</span>
        )}
        {videoPerm.canEdit && (<>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <Form.Control
            type="file"
            accept=".mp4,video/mp4"
            size="sm"
            style={{ maxWidth: 320,height:'inherit' }}
            onChange={(e: any) => setVideoFile(e.target.files?.[0] || null)}
          />
          <Button variant="primary" size="sm" style={btn} disabled={!videoFile || videoBusy} onClick={handleVideoUpload}>
            {videoBusy ? <Spinner animation="border" size="sm" /> : 'Upload'}
          </Button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 6 }}>Supports .mp4</div>
        </>)}
      </div>
      )}

      {/* ---- Rubric ---- */}
      {rubricPerm.visible && (
      <div style={card}>
        <div style={titleStyle}>Rubric</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
                <th style={{ padding: '6px 8px', width: 90 }}>Score</th>
                <th style={{ padding: '6px 8px' }}>Evaluation Criteria</th>
                {rubricPerm.canEdit && <th style={{ padding: '6px 8px', width: 110 }}>Action</th>}
              </tr>
            </thead>
            <tbody>
              {rubrics.length === 0 && (
                <tr><td colSpan={rubricPerm.canEdit ? 3 : 2} style={{ padding: '8px', color: 'var(--text-faint)' }}>No rubric criteria added</td></tr>
              )}
              {rubrics.map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid #eef2f7' }}>
                  <td style={{ padding: '6px 8px' }}>{r.score}</td>
                  <td style={{ padding: '6px 8px' }}>{r.evaluationCriteria}</td>
                  {rubricPerm.canEdit && (
                  <td style={{ padding: '6px 8px' }}>
                    <Button variant="primary" size="sm" style={btn} onClick={() => startEditRubric(r)}>Edit</Button>
                    <Button variant="danger" size="sm" style={btn} onClick={() => setDeleteRubricId(r.id)}>Delete</Button>
                  </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Add / edit rubric form */}
        {rubricPerm.canEdit && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Form.Control
            type="number"
            size="sm"
            placeholder="Score"
            value={rubricScore}
            style={{ maxWidth: 100 }}
            onChange={(e) => setRubricScore(e.target.value)}
          />
          <Form.Control
            as="textarea"
            rows={1}
            size="sm"
            placeholder="Evaluation criteria"
            value={rubricCriteria}
            style={{ flex: 1, minWidth: 220 }}
            onChange={(e) => setRubricCriteria(e.target.value)}
          />
          <Button variant="primary" size="sm" style={btn} disabled={rubricBusy} onClick={handleRubricSubmit}>
            {rubricBusy ? <Spinner animation="border" size="sm" /> : (editingRubricId ? 'Update' : 'Add Rubric')}
          </Button>
          {editingRubricId && (
            <Button variant="secondary" size="sm" style={btn} onClick={resetRubricForm}>Cancel</Button>
          )}
        </div>
        )}
      </div>
      )}

      {/* ---- Reviews ---- */}
      {reviewPerm.visible && (
      <div style={card}>
        <div style={titleStyle}>Reviews</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
                <th style={{ padding: '6px 8px', width: 160 }}>Reviewed By</th>
                <th style={{ padding: '6px 8px', width: 170 }}>Date</th>
                <th style={{ padding: '6px 8px' }}>Review</th>
              </tr>
            </thead>
            <tbody>
              {reviews.length === 0 && (
                <tr><td colSpan={3} style={{ padding: '8px', color: 'var(--text-faint)' }}>No reviews yet</td></tr>
              )}
              {reviews.map((rv, i) => (
                <tr key={i} style={{ borderTop: '1px solid #eef2f7' }}>
                  <td style={{ padding: '6px 8px' }}>{rv.commentedByName || '—'}</td>
                  <td style={{ padding: '6px 8px' }}>{formatDate(rv.commentedDate)}</td>
                  <td style={{ padding: '6px 8px' }}>{rv.comments}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {reviewPerm.canEdit && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Form.Control
            as="textarea"
            rows={1}
            size="sm"
            placeholder="Add a review comment"
            value={newReview}
            style={{ flex: 1, minWidth: 220 }}
            onChange={(e) => setNewReview(e.target.value)}
          />
          <Button variant="primary" size="sm" style={btn} disabled={reviewBusy} onClick={handleAddReview}>
            {reviewBusy ? <Spinner animation="border" size="sm" /> : 'Add Review'}
          </Button>
        </div>
        )}
      </div>
      )}

      {/* Delete rubric confirmation */}
      <Modal show={deleteRubricId != null} onHide={() => setDeleteRubricId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>Delete Rubric</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this rubric criterion?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" style={btn} onClick={() => setDeleteRubricId(null)}>Cancel</Button>
          <Button variant="danger" size="sm" style={btn} onClick={confirmDeleteRubric}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default QuestionExtrasPanel;
